import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getTaxSettings,
  updateTaxSettings,
  getContractors,
  createContractor,
  deleteContractor,
  get1099Prep,
  getScheduleCSummary,
  getUsDeadlines,
  getUsFilingDrafts,
  getStateEstimate,
} from '../api/tax';

export function useTaxSettings() {
  return useQuery({
    queryKey: ['tax', 'settings'],
    queryFn: getTaxSettings,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateTaxSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateTaxSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tax', 'settings'] });
      toast.success('Tax settings updated');
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to update tax settings');
    },
  });
}

export function useContractors() {
  return useQuery({
    queryKey: ['tax', 'contractors'],
    queryFn: getContractors,
    staleTime: 2 * 60 * 1000,
  });
}

export function useCreateContractor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createContractor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tax', 'contractors'] });
      toast.success('Contractor added');
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to add contractor');
    },
  });
}

export function useDeleteContractor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteContractor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tax', 'contractors'] });
      toast.success('Contractor removed');
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to remove contractor');
    },
  });
}

export function use1099Prep(taxYear: number) {
  return useQuery({
    queryKey: ['tax', '1099-prep', taxYear],
    queryFn: () => get1099Prep(taxYear),
    staleTime: 2 * 60 * 1000,
  });
}

export function useScheduleCSummary(taxYear: number) {
  return useQuery({
    queryKey: ['tax', 'schedule-c', taxYear],
    queryFn: () => getScheduleCSummary(taxYear),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUsDeadlines() {
  return useQuery({
    queryKey: ['tax', 'deadlines'],
    queryFn: getUsDeadlines,
    staleTime: 10 * 60 * 1000,
  });
}

export function useUsFilingDrafts() {
  return useQuery({
    queryKey: ['tax', 'filing-drafts'],
    queryFn: () => getUsFilingDrafts(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useStateEstimate(taxYear?: number) {
  return useQuery({
    queryKey: ['tax', 'state-estimate', taxYear],
    queryFn: () => getStateEstimate(taxYear),
    staleTime: 10 * 60 * 1000,
  });
}
